import { createContext, useContext, useEffect, useReducer } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';

import { useAuth } from '../../context/Auth';
import { useUserStrategies } from '../../context/UserStrategies';
import { useTutorialContext } from '../../context/Tutorial';

import { NewStrategy } from './NewStrategy';

import { getTotalValues } from '../../helpers/functions';

import { ContextState as State, NavStep, NewStrategyValues } from './types';

import { CURRENCIES } from '../../constants/lists';

import {
  CLONE_STRATEGY_ASSET,
  CREATE_STRATEGY,
  CREATE_STRATEGY_ASSETS,
  DELETE_STRATEGY,
  DELETE_STRATEGY_ASSET,
  DELETE_STRATEGY_ASSETS,
  LIST_DEFAULT_WORKSPACES,
  SET_STRATEGY_STATUS,
  SET_STRATEGY_WORKSPACE,
  UPDATE_STRATEGY,
} from '../../http/apollo-queries';
import { IWorkspace, TTickerCurrency } from '../../types/model';
import { useTranslation } from 'react-i18next';
import { logger } from '../../helpers/logger';
import {
  ConnectionTypeEnum,
  CurrencyCodeEnum,
  CurrencySymbolEnum,
} from '../../constants/enums';

const reducer = (state: State, updates: Partial<State>): State => {
  return { ...state, ...updates };
};

const INITIAL_NEW_STRATEGY: NewStrategyValues = {
  assets: [],
  reportingCurrency: {
    code: CurrencyCodeEnum.USD,
    symbol: CurrencySymbolEnum.USD,
  },
  strategyColor: undefined,
  strategyId: '',
  strategyName: '',
  strategyTimeframe: [
    new Date(),
    new Date(moment().add(1, 'y').format('YYYY/MM/DD')),
  ],
  tagList: [],
  timeframeAlert: false,
  totalValueNewStrategy: 0,
  workspaceId: null,
  currentWorkspaceId: undefined,
  flow: 'SCRATCH',
};

// this state will be updated by the 'reducer' function
const localState: State = {
  loading: false,
  flow: 'SCRATCH',
  isDisabledNext: false,
  isFirstTime: false,
  isTooltipIconVisible: false,
  mode: ConnectionTypeEnum.MANUAL,
  allSteps: [],
  flowSteps: [],
  newStrategy: INITIAL_NEW_STRATEGY,
  currentStep: 0,
  selectedConnectionType: ConnectionTypeEnum.MANUAL,
  title: '',
  totalValueNewStrategy: 0,
  dispatch: () => {},
};

const NewStrategyContext = createContext<State>(localState);

const NewStrategyProvider = ({
  children,
  closeModal,
}: {
  children: JSX.Element;
  closeModal(): void;
}) => {
  const [state, dispatch] = useReducer(reducer, localState);

  const { t } = useTranslation();
  const { user } = useAuth();
  const { handleTooltipIconVisibility } = useTutorialContext();

  const {
    draftStrategy,
    draftCloneStrategy,
    loading: loadingStrategies,
    refetch: refetchStrategies,
  } = useUserStrategies();

  const allSteps: NavStep[] = [
    {
      key: 'START',
      title: '',
      showInNavbar: false,
      flows: ['SCRATCH', 'CLONE'],
    },
    {
      key: 'CREATE_STRATEGY',
      title: t('strategies.newStrategy.steps.createStrategy'),
      showInNavbar: true,
      flows: ['SCRATCH', 'CLONE'],
    },
    {
      key: 'ADD_FROM_STRATEGIES',
      title: t('strategies.newStrategy.steps.duplicateOrImport'),
      showInNavbar: true,
      flows: ['CLONE'],
    },
    {
      key: 'IMPORT_MODE',
      title: t('strategies.newStrategy.steps.chooseImportMode'),
      showInNavbar: true,
      flows: ['SCRATCH'],
    },
    {
      key: 'ADD_ASSETS',
      title: t('strategies.newStrategy.steps.chooseAndAddAssets'),
      showInNavbar: true,
      flows: ['SCRATCH', 'CLONE'],
    },
  ];

  const defaultFlowSteps = allSteps.filter(({ flows }) =>
    flows.includes(state.flow)
  );

  const setFlow = (flow) => {
    const flowSteps = allSteps.filter(({ flows }) => flows.includes(flow));
    logger.log('NewStrategy:SetFlow', {
      flow,
      allSteps: allSteps.length,
      flowSteps,
    });
    dispatch({
      flow,
      flowSteps: allSteps.filter(({ flows }) => flows.includes(flow)),
    });
  };

  logger.log('NewStrategyContext:Drafts', {
    scratch: !!draftStrategy?.id,
    clone: !!draftCloneStrategy?.id,
  });

  const resetNewStrategy = () => {
    dispatch({ newStrategy: INITIAL_NEW_STRATEGY });
  };

  //Update state on user or draft strategy changes
  useEffect(() => {
    const strategy =
      state.flow === 'CLONE' ? draftCloneStrategy : draftStrategy;

    logger.log('NewStrategyContext:update', strategy?.name);
    if (strategy) {
      dispatch({
        newStrategy: {
          ...state.newStrategy,
          currentWorkspaceId: strategy.workspaceId,
          assets: [
            ...(strategy.strategyAssets?.items || state.newStrategy.assets),
          ],
          ...(strategy.currency && {
            reportingCurrency: CURRENCIES.find(
              ({ code }) => strategy.currency === code
            )!,
          }),
          strategyColor: strategy.color || state.newStrategy.strategyColor,
          strategyId: strategy.id || state.newStrategy.strategyId,
          strategyName: strategy.name || state.newStrategy.strategyName,
          strategyTimeframe: [
            strategy.startDate
              ? new Date(strategy.startDate)
              : state.newStrategy.strategyTimeframe[0],
            strategy.endDate
              ? new Date(strategy.endDate)
              : state.newStrategy.strategyTimeframe[1],
          ],
          tagList: strategy.tags || state.newStrategy.tagList,
          timeframeAlert:
            strategy.notifyEnd || state.newStrategy.timeframeAlert,
        },
      });
    } else {
      resetNewStrategy();
    }
  }, [draftStrategy, draftCloneStrategy, state.flow, user]);

  const {
    data: { listWorkspaces: { items: workspacesList = [] } = {} } = {},
    loading: loadingWorkspaces,
  } = useQuery<{ listWorkspaces: { items: IWorkspace[] } }>(
    LIST_DEFAULT_WORKSPACES
  );

  const [createStrategy, { loading: createStrategyLoading }] =
    useMutation(CREATE_STRATEGY);
  const [updateStrategy, { loading: updateStrategyLoading }] =
    useMutation(UPDATE_STRATEGY);
  const [updateWorkspace, { loading: updateWorkspaceLoading }] = useMutation(
    SET_STRATEGY_WORKSPACE
  );

  const [setStrategyStatus] = useMutation(SET_STRATEGY_STATUS);

  const [deleteStrategyAsset, { loading: loadingDeleteStrategyAsset }] =
    useMutation(DELETE_STRATEGY_ASSET);

  const [deleteStrategyAssets, { loading: loadingDeleteStrategyAssets }] =
    useMutation(DELETE_STRATEGY_ASSETS);

  const [createStrategyAssets, { loading: loadingCreateStrategyAssets }] =
    useMutation(CREATE_STRATEGY_ASSETS);

  const [cloneStrategyAsset, { loading: loadingCloneStrategyAsset }] =
    useMutation(CLONE_STRATEGY_ASSET, {
      onCompleted: () => refetchStrategies(),
    });

  const [deleteStrategy, { loading: loadingDeleteStrategy }] =
    useMutation(DELETE_STRATEGY);

  const closeConfirmModal = () => dispatch({ confirmElement: undefined });

  const skipStrategy = () => {
    user?.profile?.showTutorial?.STRATEGIES &&
      handleTooltipIconVisibility('STRATEGIES');
    closeModal();
  };

  const { totalValue: totalValueNewStrategy } = getTotalValues(
    state.newStrategy.assets,
    (
      state.newStrategy.reportingCurrency?.code || CurrencyCodeEnum.USD
    ).toLocaleLowerCase() as TTickerCurrency,
    moment().format('YYYY-MM-DD')
  );

  const loading =
    loadingStrategies ||
    loadingWorkspaces ||
    loadingCreateStrategyAssets ||
    loadingDeleteStrategyAsset ||
    loadingDeleteStrategyAssets ||
    loadingCloneStrategyAsset ||
    loadingDeleteStrategy ||
    createStrategyLoading ||
    updateStrategyLoading ||
    updateWorkspaceLoading;

  const value = {
    ...state,
    loading,
    totalValueNewStrategy,
    workspacesList,
    closeConfirmModal,
    closeModal,
    allSteps,
    flowSteps: defaultFlowSteps,
    createStrategy,
    createStrategyAssets,
    deleteStrategyAssets,
    deleteStrategyAsset,
    deleteStrategy,
    cloneStrategyAsset,
    dispatch,
    setFlow,
    resetNewStrategy,
    setStrategyStatus,
    skipStrategy,
    updateStrategy,
    updateWorkspace,
  };

  return (
    <NewStrategyContext.Provider value={value}>
      {children}
    </NewStrategyContext.Provider>
  );
};

export const NewStrategyPage = ({ closeModal }: { closeModal(): void }) => (
  <NewStrategyProvider closeModal={closeModal}>
    <NewStrategy />
  </NewStrategyProvider>
);

export const useNewStrategyContext = () => useContext(NewStrategyContext);
